import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { useLocation } from 'react-use';
import type { UserPermissionsMap } from '../../../access-control/types';
import { InterfaceModes } from '../../../stores/InterfaceStore/types';
import { getResolutionByDevice } from '../../../utils/getResolutionByDevice';
import { attachComputedNavigationToInterfaceRecord } from './getInterfaceRecordForState';
// oxlint-disable-next-line no-cycle -- see getInitialPageVariablesState import; nav types decoupled in navigationForContextTypes
import { getNavigationForContext } from './getNavigationForContext';

function getInterfaceBasePathForComputeContext(interfaceRecord: InterfaceEntity | undefined) {
  const interfaceProperties = interfaceRecord?.properties;
  const basePath = interfaceProperties?.basePath || interfaceProperties?.navigation?.basePath;

  if (basePath) {
    return basePath.startsWith('/') ? basePath : `/${basePath}`;
  }

  return undefined;
}

type ComputeContextDeviceDetailsSlice = {
  device: DeviceVariantType;
};

type ComputeContextLocationSlice = Pick<
  ReturnType<typeof useLocation>,
  'href' | 'hostname' | 'pathname' | 'search' | 'hash'
> & {
  pathParams?: Record<string, string | string[] | undefined>;
};

/**
 * App (and optional page) inputs used to derive navigation and attach it on a copy of the interface
 * entity under `properties.navigation` for `getInterfaceRecordForState`.
 */
export type AppGlobalStateSliceForComputeContext = {
  deviceDetails: ComputeContextDeviceDetailsSlice;
  mode: InterfaceModes;
  permissions: UserPermissionsMap;
  navigation: {
    isDrawerOpen: boolean;
    isNotificationPanelOpen: boolean;
    unopenedNotificationsCount: number;
  };
  location: ComputeContextLocationSlice;
  basePath?: string;
  interfaceRecord?: InterfaceEntity;
  activeAppVariantKey?: string;
};

export interface GetInterfaceRecordStateForComputeContextArgs {
  appGlobalState?: AppGlobalStateSliceForComputeContext;
  queryBasePath?: string;
  interfaceRecord?: InterfaceEntity;
  interfaceMode?: InterfaceModes;
}

export function getInterfaceRecordForComputeContext({
  appGlobalState,
  queryBasePath,
  interfaceRecord: interfaceRecordOverride,
  interfaceMode: interfaceModeOverride,
}: GetInterfaceRecordStateForComputeContextArgs): InterfaceEntity | undefined {
  const record = interfaceRecordOverride ?? appGlobalState?.interfaceRecord;
  if (!record) {
    return undefined;
  }

  const resolution = getResolutionByDevice(
    appGlobalState?.deviceDetails?.device ?? DeviceVariantType.DESKTOP,
    appGlobalState?.mode,
  );
  const interfaceMode = interfaceModeOverride ?? appGlobalState?.mode ?? InterfaceModes.RUNNER;
  const basePath =
    queryBasePath ?? appGlobalState?.basePath ?? getInterfaceBasePathForComputeContext(record);

  const navigation = getNavigationForContext({
    interfaceRecord: record,
    resolution,
    applicationType: record.properties?.manifest?.type,
    interfaceMode,
    permissions: appGlobalState?.permissions,
    isNotificationPanelOpen: appGlobalState?.navigation?.isNotificationPanelOpen,
    unopenedNotificationsCount: appGlobalState?.navigation?.unopenedNotificationsCount,
    location: appGlobalState?.location,
    basePath,
  });

  return attachComputedNavigationToInterfaceRecord(record, navigation);
}
